/** @jsx jsx */
import { Fragment, h } from 'preact'
import { useState } from 'preact/hooks'
import { useRef } from 'react';

// Searchspring
import { Price, Results, useMediaQuery } from '@searchspring/snap-preact-components'
import { useTheme, CacheProvider } from '@searchspring/snap-preact-components/dist/cjs/providers'
import { defined, cloneWithProps } from '@searchspring/snap-preact-components/dist/cjs/utilities'

// Packages
import { jsx, css } from '@emotion/react'
import classnames from 'classnames'
import { observer } from 'mobx-react-lite'

// Local 
import { useSwatches } from '../Helpers'
import { IconMore } from '../Icons/icon_more'
import { YotpoStar, getYotpoStarVariants } from '../Icons/YotpoStar';

// CSS
import './result.css'
import { useLenghts } from '../Helpers/useLengths';
import WoodcraftConstants from '../Helpers/constants';

const CSS = {
    result: () =>
        css({
            '&.ss__result--grid': {
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                '& .ss__result__image-wrapper': {
                    flex: '1 0 auto',
                },
            },
            '&.ss__result--list': {
                display: 'flex',
                flexDirection: 'row',
                '& .ss__result__image-wrapper': {
                    flex: '0 0 33%',
                },
                '& .ss__result__details': {
                    flex: '1 1 auto',
                    textAlign: 'left',
                    marginLeft: '20px',
                    padding: 0,
                },
            },

            '& .ss__result__image-wrapper': {
                position: 'relative',
                '& .ss__result__badge': {
                    background: '#FFFFFF',
                },
            },

            '& .ss__result__details': {
                textAlign: 'center',
                '& .ss__result__details__pricing': {
                    '& .ss__result__price': {
                        fontSize: '1.2em',
                    },
                    '& .ss__price--strike': {
                        fontSize: '80%',
                    },
                },
                '& .ss__result__details__button': {
                    marginBottom: '10px',
                },
            },
        }),
}

export const Result = observer(properties => {
    const globalTheme = useTheme()
    const [swatchIndex, setSwatchIndex] = useState(0)
    const [lengthIndex, setLengthIndex] = useState(0)



    const props = {
        layout: 'grid',
        ...globalTheme?.components?.result,
        ...properties,
        ...properties.theme?.components?.result,
    }

    const {
        result,
        hideBadge,
        hideSwatches,
        hideLengths,
        hidePricing,
        hideTitle,
        hideWishlist,
        hideReviews,
        isRecommendations,
        detailSlot,
        fallback,
        disableStyles,
        className,
        layout,
        style,
        controller,
    } = props
    
    const dataFirstNewResult = props['data-first-new-result']

    const core = result?.mappings?.core
    const productID = core.uid;
    const hoverImage = result?.attributes?.ss_image_hover;
    const badge = result?.attributes?.mfield_woodcraft_product_badge ? result?.attributes?.mfield_woodcraft_product_badge.toString() : null
    const promo_message = result?.attributes?.mfield_woodcraft_promo_message ? result?.attributes?.mfield_woodcraft_promo_message.toString() : null
    const variantColors = result?.attributes?.variant_color
    const variantImages = result?.attributes?.variant_images_json
    const vendor = result?.attributes?.vendor
    const variantLength = result?.attributes?.variant_length;
    const swatchImages = result?.attributes.mfield_cql_swatches_json
    const reviewScore =
        Number(result?.attributes?.mfield_yotpo_reviews_average ?? 0) || 0;
    const reviewTotal =
        Number(result?.attributes?.mfield_yotpo_reviews_count ?? 0) || 0;

    const showPrice = (String(result?.attributes.mfield_woodcraft_see_price_in_cart).toLowerCase() === 'true');
    const variantsArr = result?.attributes?.variant_sku;
    let shouldSkip = false;
    const compareAtPrice = result?.attributes?.variant_compare_at_price;

    if (variantImages) {
      var parsedImages = JSON.parse(variantImages);
      var filteredImages = removeDuplicateObjects(parsedImages, 'color');
    }
  
    function removeDuplicateObjects(arr, key) {
      const seen = new Set();
      const filteredArr = arr.filter(obj => {
        const objKey = key ? obj[key] : JSON.stringify(obj);
        if (!seen.has(objKey)) {
          seen.add(objKey);
          return true;
        }
        return false;
      });
    
      return filteredArr;
    }

    const swatches = useSwatches({
      variantColors: result.attributes.variant_color,
      variantImages: filteredImages,
      fallbackImage: core.imageUrl,
      swatchOptions1: JSON.parse(window.Resources.swatches_1.textContent),
      productUrl: core.url,
    })

    const lengths = useLenghts({
      variantLength: variantLength,
      productUrl: core.url
    })
  

    const subProps = {
        price: {
            className: 'ss__result__price',
            ...globalTheme?.components?.price,
            ...defined({
                disableStyles,
            }),
            theme: props.theme,
        },
        badge: {
            className: 'ss__result__badge',
            content: badge,
            ...globalTheme?.components?.badge,
            ...defined({
                disableStyles,
            }),
            theme: props.theme,
        },
        image: {
            className: 'ss__result__image',
            alt: core?.name,
            src: filteredImages?.[swatchIndex]?.img ? filteredImages?.[swatchIndex]?.img : core?.imageUrl,
            ...globalTheme?.components?.image,
            ...defined({
                disableStyles,
                fallback: fallback,
            }),
            theme: props.theme,
        },
    }

    const styling = {}
    if (!disableStyles) {
        styling.css = [CSS.result(), style]
    } else if (style) {
        styling.css = [style]
    }

    // const queryParam = useMemo(() => {
    //     if (![...swatches][swatchIndex]) return ''
    //     return `?color=${[...swatches][swatchIndex]?.color}`
    // }, [swatches, swatchIndex])

    const priceStrArr = core.price.toString().split('.');
    const priceDollars = priceStrArr[0];
    const priceCents = (priceStrArr[1] ?? '00').padEnd(2, '0').slice(0, 2);

    const savings = (Math.round((core.msrp - core.price) * 100) / 100).toFixed(2);
    const savingsPercentageValue = savings * 100;
    const savingsPercentage = (Math.round((savingsPercentageValue / core.msrp) * 100) / 100).toFixed(0);

    const normalizedName = core.name.trim().toLowerCase();
    const hideWishListInCard = normalizedName.includes('gift card') || normalizedName.includes('giftcard') || normalizedName.includes('class fee');

    const displayPrices = () => {
        const priceStrArr = core.price.toString().split('.');
        const priceDollars = priceStrArr[0];
        const priceCents = (priceStrArr[1] ?? '00').padEnd(2, '0').slice(0, 2);

        const savings = (Math.round((core.msrp - core.price) * 100) / 100).toFixed(2);
        const savingsPercentageValue = savings * 100;
        const savingsPercentage = (Math.round((savingsPercentageValue / core.msrp) * 100) / 100).toFixed(0);

        if (compareAtPrice > core.price && showPrice) {
            let intCompareAtPrice = Number(compareAtPrice)
            const comparePriceRounded = Math.floor(intCompareAtPrice);
            const compareAtPriceDisplay = comparePriceRounded.toFixed(0)
            const cents = (intCompareAtPrice - comparePriceRounded).toFixed(2)
            const dotIndex = cents.indexOf('.');
            const centsDisplay = (cents.slice(dotIndex + 1) || '00').padEnd(2, '0').slice(0, 2);

            return (
              <>
                <div className='ss__see-in-cart'>
                    <div className={`ss__starts-at-price ss__price-range-wrapper--custom`}>
                        <span>
                            <s class="see-in-cart-price">
                                <span class="xs">{WoodcraftConstants.DOLLAR_SIGN}</span>
                                <span class="dollars">{compareAtPriceDisplay}</span>
                                <span class="sr-only">{WoodcraftConstants.CURRENCY_SEPARATOR}</span>
                                <span class="xs">{centsDisplay}</span>
                            </s>
                        </span> 
                        <span class="ss__see-in-cart_label"> { window.Resources.searchspring.result.seeCart } </span>
                    </div>
                </div>
              </>
          )
        }

        return (
            <>
                <div className="sale-price">
                    <span className="xs">{WoodcraftConstants.DOLLAR_SIGN}</span>
                    <span className="dollars">{priceDollars}</span>
                    <span className="sr-only">{WoodcraftConstants.CURRENCY_SEPARATOR}</span>
                    <span className="xs">{priceCents}</span>
                </div>

                {savingsPercentage > 0 && (
                    <div className="savings-info">
                        <div className="original-price">
                            {WoodcraftConstants.WAS} <s>{WoodcraftConstants.DOLLAR_SIGN}{core.msrp.toFixed(2)}</s>
                        </div>
                        <div className="savings">
                            {WoodcraftConstants.SAVE} ${savings} ({savingsPercentage}{WoodcraftConstants.PERCENT_OFF})
                        </div>
                    </div>
                )}
            </>
        )
    }

    const articleProps = {
        ...styling,
        className: classnames('ss__result', (typeof hoverImage != "undefined" && 'ss__result__hover'), `ss__result--${layout}`, className),
    }
    
    if (dataFirstNewResult) {
        articleProps['data-first-new-result'] = dataFirstNewResult
    }

    return (
        <CacheProvider>
            <article
                {...articleProps}
            >
                <a
                    href={core.url}
                    onMouseDown={e => {
                        controller?.track?.product?.click(e, result)
                    }}
                    className={`ss__result-link ${hideWishListInCard ? 'ss__hide-wishlist' : ''}`}
                    aria-label={core.name}
                >
                    <div className="product-card-top-wrapper">
                        <div className="ss__result__image-wrapper">
                            {!hideBadge && (
                                <div className={classnames('ss__badge ss__result__badge', {
                                    'ss__badge-red': badge === "Sale" || badge === "Clearance"
                                })}>
                                    {badge?.toUpperCase()}
                                </div>
                            )}

                            <div
                                {...styling}
                                className={classnames('ss__image', 'ss__result__image')}
                            >

                                <img
                                  id={`ProductImage-${result.id}`}
                                  alt={core.name}
                                  class="product__img product__img--main"
                                  data-aspectratio="1.4967637540453074"
                                  data-sizes="auto"
                                  loading={props.index < 3 ? 'eager' : 'lazy'}
                                  fetchpriority={props.index < 3 ? 'high' : 'auto'}
                                  data-image
                                  src={ filteredImages?.[swatchIndex]?.img ? `${filteredImages?.[swatchIndex]?.img}&width=200&quality=85` : `${core?.imageUrl}&width=200&quality=85`} 
                                  srcSet={ filteredImages?.[swatchIndex]?.img ? 
                                    `${filteredImages?.[swatchIndex]?.img}&width=160&quality=85 160w, ${filteredImages?.[swatchIndex]?.img}&width=240&quality=85 240w, ${filteredImages?.[swatchIndex]?.img}&width=300&quality=85 300w, ${filteredImages?.[swatchIndex]?.img}&width=335&quality=85 335w` : 
                                    `${core?.imageUrl}&width=160&quality=85 160w, ${core?.imageUrl}&width=240&quality=85 240w, ${core?.imageUrl}&width=300&quality=85 300w, ${core?.imageUrl}&width=335&quality=85 335w`
                                  }
                                  sizes="(max-width: 750px) 50vw, (max-width: 1422px) calc(33vw - 2rem), calc(25vw - 1rem)"
                                /> 
                                
                                {typeof hoverImage != "undefined" &&
                                    <img
                                    id={`ProductImage-${result.id}`}
                                    alt={core.name}
                                    class="product__img product__img--hover"
                                    data-aspectratio="1.4967637540453074"
                                    data-sizes="auto"
                                    loading={props.index < 3 ? 'eager' : 'lazy'}
                                    data-image
                                    src={`${hoverImage}&width=200&quality=85`}
                                    srcSet={`${hoverImage}&width=160&quality=85 160w, ${hoverImage}&width=240&quality=85 240w, ${hoverImage}&width=300&quality=85 300w, ${hoverImage}&width=335&quality=85 335w`}
                                    sizes="(max-width: 750px) 50vw, (max-width: 1422px) calc(33vw - 2rem), calc(25vw - 1rem)"
                                  />
                                }

                                {variantsArr?.length > 1 && (
                                <div className="options-badge">
                                    <p>{variantsArr.length} {WoodcraftConstants.OPTIONS}</p>
                                </div>
                                )}
                            </div>
                        </div>
                        <div className="ss__result__details">
                            { vendor ?  
                              <span className="ss__vendor">{vendor}</span> 
                              : null 
                            }
                            {!hideTitle && (
                                <div
                                    className="ss__result__details__title"
                                    dangerouslySetInnerHTML={{
                                        __html: core.name,
                                    }}
                                />
                            )}
                            {!hideReviews && (
                                <div className="ss__result__details__review">
                                    {/* Leaving this comment below for now */}
                                    {/* <div class="yotpo bottomLine" data-product-id={ core.uid }></div> */}

                                    {getYotpoStarVariants(reviewScore).map((variant, starIndex) => (
                                        <YotpoStar
                                            key={`${productID}-${starIndex}-${variant}`}
                                            variant={variant}
                                            idSuffix={`${productID}-${starIndex}`}
                                        />
                                    ))}

                                    {reviewTotal > 0 && reviewTotal !== undefined && (
                                        <span className="ss__results-count">
                                            ({reviewTotal}{' '}
                                            {reviewTotal == 1
                                                ? WoodcraftConstants.REVIEW
                                                : WoodcraftConstants.REVIEWS})
                                        </span>
                                    )}

                                    {reviewTotal == 0 && (
                                        <span className="ss__results-count">
                                            {WoodcraftConstants.ZERO_REVIEWS}
                                        </span>
                                    )}
                                </div>
                            )}  
                            {!hidePricing && (
                                <div className="pricing">
                                    {displayPrices()}
                                </div>
                            )}
                            {isRecommendations && (
                                <div className="ss__result__details__pricing">
                                    {displayPrices()}
                                </div>
                            )} 
                            {promo_message !=null && (
                                <div className="ss__result__details__promo-msg">
                                    <span  dangerouslySetInnerHTML={{ __html: promo_message }}></span>
                                </div>
                            )} 
                            {!hideLengths && lengths.size > 0 ? (
                                <LengthSwatches
                                    lengths={lengths}
                                    setLengthIndex={setLengthIndex}
                                    lengthIndex={lengthIndex}
                                    core={core}
                                />
                            ) : null}                        
                            {cloneWithProps(detailSlot, { result })}
                        </div>
                    </div>
                    </a>
                    <div className="product-card-bottom-wrapper">
                  
                        {!hideSwatches && swatches.size > 1 ? (
                            <ColorSwatches
                                swatches={swatches}
                                setSwatchIndex={setSwatchIndex}
                                swatchIndex={swatchIndex}
                                core={core}
                            />
                        ) : null}
                        {result.attributes.variant_mfield_woodcraft_shipping_fee_message && result.attributes.variant_mfield_woodcraft_shipping_fee_message.toLowerCase().includes('free')? 
                        <div className="product-card-free-shipping-message">
                            <svg xmlns="http://www.w3.org/2000/svg" width="19" height="13" viewBox="0 0 19 13" fill="none">
                                <path d="M5.25 12.5C4.55556 12.5 3.96528 12.2569 3.47917 11.7708C2.99306 11.2847 2.75 10.6944 2.75 10H1L1.5 8.5H3.25C3.48611 8.19444 3.77944 7.95139 4.13 7.77083C4.48042 7.59028 4.85375 7.5 5.25 7.5C5.64625 7.5 6.01958 7.59028 6.37 7.77083C6.72056 7.95139 7.01389 8.19444 7.25 8.5H11.3333L13.0833 1.5H4L4.16667 1C4.27778 0.694444 4.46181 0.451389 4.71875 0.270833C4.97569 0.0902778 5.26389 0 5.58333 0H15L14.25 3H16L18.5 6.5L17.5 10H16.5C16.5 10.6944 16.2569 11.2847 15.7708 11.7708C15.2847 12.2569 14.6944 12.5 14 12.5C13.3056 12.5 12.7153 12.2569 12.2292 11.7708C11.7431 11.2847 11.5 10.6944 11.5 10H7.75C7.75 10.6944 7.50694 11.2847 7.02083 11.7708C6.53472 12.2569 5.94444 12.5 5.25 12.5ZM13.25 7H16.7917L16.8542 6.77083L15.2292 4.5H13.875L13.25 7ZM13 1.83333L13.0833 1.5L11.3333 8.5L11.4167 8.16667L12.125 5.33333L13 1.83333ZM0 7.16667L0.375 5.66667H4.5L4.125 7.16667H0ZM1.5 4.33333L1.875 2.83333H6.75L6.375 4.33333H1.5ZM5.25 11C5.53333 11 5.77083 10.9042 5.9625 10.7125C6.15417 10.5208 6.25 10.2833 6.25 10C6.25 9.71667 6.15417 9.47917 5.9625 9.2875C5.77083 9.09583 5.53333 9 5.25 9C4.96667 9 4.72917 9.09583 4.5375 9.2875C4.34583 9.47917 4.25 9.71667 4.25 10C4.25 10.2833 4.34583 10.5208 4.5375 10.7125C4.72917 10.9042 4.96667 11 5.25 11ZM14 11C14.2833 11 14.5208 10.9042 14.7125 10.7125C14.9042 10.5208 15 10.2833 15 10C15 9.71667 14.9042 9.47917 14.7125 9.2875C14.5208 9.09583 14.2833 9 14 9C13.7167 9 13.4792 9.09583 13.2875 9.2875C13.0958 9.47917 13 9.71667 13 10C13 10.2833 13.0958 10.5208 13.2875 10.7125C13.4792 10.9042 13.7167 11 14 11Z" fill="#202020"/>
                            </svg>
                            <div>{result.attributes.variant_mfield_woodcraft_shipping_fee_message}</div>
                        </div>
                        : null
                        }
                    </div>
            </article>
        </CacheProvider>
    )
})

function convertInchesLengthStringToNumericValue(length) {
    // handle non-whole-number and partial lengths. Ex. 1/2, 1-1/4, 4-7/16 etc.
    const sum = length.split('-').reduce((acc, current) => {
        const partial = current.split('/')
        if (partial?.length > 1) {
            return acc + partial.reduce((a, c) => {
                return +a / +c;
            })
        }
        const addTo = +current;
        return acc + addTo
    }, 0)
    return {
        display: `${length}”`,
        value: sum,
    }
}

const LengthSwatches = ({ lengths, setLengthIndex, lengthIndex, core }) => {
    const lengthList = [...lengths];
  
    const items = lengthList
      .map(item => {
        // process as inches
        if (item?.length.includes('"')) {
            return convertInchesLengthStringToNumericValue(item.length.replace('"', '').trim())
        }
        // assuming metric otherwise. ex: 10 mm
        return {
            display: item?.length,
            value: +item?.length.match(/\d*\.?\d+/gm)?.join('')
        }
      }) 
      .filter(length => {
        return !isNaN(length.value)
      })
      .sort((a, b) => {
        return a.value - b.value
      })
  
    // If there are valid numeric lengths
    //Testing SS Changes 1.2.3
    if (items?.length > 0) {
      const minLength = items[0]
      const maxLength = items[items.length -1];

      if (lengthList.length === 1 || minLength.display === maxLength.display) {
        return (
          <div className="ss__result__length-swatches">
            <div className='length-swatches-availibilty'>
              {window.Resources.searchspring.result.availableIn} {minLength.display} {window.Resources.searchspring.result?.length}
            </div>
          </div>
        );
      } else {
        return (
          <div className="ss__result__length-swatches">
            <div className='length-swatches-availibilty'>
              {window.Resources.searchspring.result.availableIn} {minLength.display} - {maxLength.display} {window.Resources.searchspring.result.lengths}
            </div>
          </div>
        );
      }
    } else {
      // Handle case when there are no valid numeric lengths
      return (
        <div className="ss__result__length-swatches">
          <div className='length-swatches-availibilty'>
            {window.Resources.searchspring.result.availableIn} (No numeric lengths available)
          </div>
        </div>
      );
    }
  };
  

const ColorSwatches = ({ swatches, setSwatchIndex, swatchIndex, core }) => {
    const isMobile = useMediaQuery('(max-width: 749px)');
    const breakNumber = isMobile ? 3 : 5;
    const swatchList = [...swatches];

    return (
        <div className="ss__result__swatches">
            {swatchList.map((swatch, index) => {
                if (index > breakNumber) return null

                let isImage = false;
                if (swatch.swatch_options.swatchImage) {
                  isImage = true;
                }

                let isFallback = false;
                if (!swatch.swatch_options.swatchImage && !swatch.swatch_options.hexValue) {
                  isFallback = true;
                }

                return (
                    <>
                        {index === breakNumber ? (
                          <a className='icon-more-link'
                             href={core.url}>
                            <IconMore
                                count = {swatchList?.length}
                                breakpoint = {breakNumber}
                            />
                          </a> 
                        ) : (
                            <div
                                className={classnames('swatch-image', {
                                    'swatch-image-selected': swatchIndex === index,
                                })}
                                title={swatch.color}
                                data-index={index}
                            >

                            {swatch.swatch_options.swatchImage ? (
                                <img
                                    src={swatch.swatch_options.swatchFile}
                                    onClick={e => {
                                        e.preventDefault()
                                        setSwatchIndex(index)
                                    }}
                                    alt={swatch.color}
                                    width={100}
                                    height={100}
                                    loading="lazy"
                                />
                            ) : 
                                null
                            }

                            {(!isImage && swatch.swatch_options.hexValue && swatch.swatch_options.hexValue != 'rgba(0,0,0,0)') ? (
                                <span className='ss__swatch-hex' style={{ background: swatch.swatch_options.hexValue }}
                                    onClick={e => {
                                        e.preventDefault()
                                        setSwatchIndex(index)
                                    }}>
                                </span>
                            ) : 
                                null
                            }

                            { isFallback && swatch?.thumbnail ? 
                              <img
                              src={`${swatch.thumbnail}&width=30`}
                              onClick={e => {
                                  e.preventDefault()
                                  setSwatchIndex(index)
                              }}
                              alt={swatch.color}
                              width={30}
                              height={30}
                              loading="lazy"
                            />
                            : null }

                            { isFallback && !swatch?.thumbnail ? 
                              <img
                                src={swatch.fallback_img}
                                alt={swatch.color}
                                width={16}
                                height={16}
                                loading="lazy"
                              />
                            : null }

                            </div>
                        )}
                    </>
                )
            })}
        </div>
    )
}